import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search as SearchIcon, Sparkles } from "lucide-react";

import useNotificationsStore, { selectNewTenderMatches } from "../../../store/useNotificationsStore";

/* Search | Discover, at the top of both tender screens. Search is the corpus by
 * hand and the default; Discover is where the workspace's tenders arrive on
 * their own. Two routes rather than one page with a mode, because Search keeps
 * its whole state in the URL and a mode switch there would fight it — so the
 * switch remembers that URL, and coming back to Search finds the same filters.
 * 35px tall, the height of the search box and Sort beside it. */

const LAST_SEARCH = "tender_search_last_query";

const remember = (query) => {
  try {
    sessionStorage.setItem(LAST_SEARCH, query || "");
  } catch {
    /* storage blocked: Search just opens fresh */
  }
};

const lastSearch = () => {
  try {
    return sessionStorage.getItem(LAST_SEARCH) || "";
  } catch {
    return "";
  }
};

export default function TenderModeSwitch({ active, newCount }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Discover passes its own count; on Search it is the unread bell entry, as on the sidebar.
  const unread = useNotificationsStore(selectNewTenderMatches);
  const count = newCount ?? unread;

  const go = (key) => {
    if (key === active) return;
    if (active === "search") remember(location.search);
    navigate(key === "search" ? `/tender-search${lastSearch()}` : "/tender-search/discover");
  };

  const tab = (key, label, Icon) => {
    const on = active === key;
    return (
      <button
        type="button"
        onClick={() => go(key)}
        aria-pressed={on}
        className={`inline-flex h-[29px] items-center gap-1.5 rounded-[7px] px-3 font-outfit text-[13.5px] font-semibold transition-colors ${
          on ? "bg-white text-[#18101C] shadow-[0_1px_3px_rgba(24,16,28,0.12)]" : "text-[#6B6472] hover:text-[#18101C]"
        }`}
      >
        <Icon className={`h-4 w-4 ${on && key === "discover" ? "text-[#A04A97]" : ""}`} strokeWidth={2} />
        {label}
        {key === "discover" && count > 0 && (
          <span className="rounded-full bg-[#A04A97] px-1.5 py-[1px] text-[11px] font-bold leading-[14px] text-white">
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      role="group"
      aria-label="Tender Search mode"
      className="inline-flex h-[35px] flex-none items-center gap-0.5 rounded-[10px] bg-[#18101C]/[0.05] p-[3px]"
    >
      {tab("search", "Search", SearchIcon)}
      {tab("discover", "Discover", Sparkles)}
    </div>
  );
}
